export const COOKIE_CONSENT_STORAGE_KEY = 'caloriescience_cookie_consent';
export const COOKIE_PREFERENCES_OPEN_EVENT = 'caloriescience:open-cookie-preferences';
export const COOKIE_CONSENT_UPDATED_EVENT = 'caloriescience:cookie-consent-updated';

export interface CookieConsentPreferences {
  necessary: true;
  analytics: boolean;
  version: number;
  updatedAt: string;
}

export type CookieConsentChoice = Pick<CookieConsentPreferences, 'analytics'>;

export const COOKIE_CONSENT_VERSION = 1;

const isBrowser = () => typeof window !== 'undefined';

const toConsentRecord = (choice: CookieConsentChoice): CookieConsentPreferences => ({
  necessary: true,
  analytics: choice.analytics,
  version: COOKIE_CONSENT_VERSION,
  updatedAt: new Date().toISOString(),
});

const isValidConsentRecord = (value: unknown): value is CookieConsentPreferences => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Partial<CookieConsentPreferences>;
  return (
    record.necessary === true &&
    typeof record.analytics === 'boolean' &&
    typeof record.version === 'number' &&
    typeof record.updatedAt === 'string'
  );
};

export const readCookieConsent = (): CookieConsentPreferences | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    return isValidConsentRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveCookieConsent = (choice: CookieConsentChoice): CookieConsentPreferences => {
  const record = toConsentRecord(choice);

  if (isBrowser()) {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_UPDATED_EVENT, { detail: record }));
  }

  return record;
};

export const openCookiePreferences = () => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new Event(COOKIE_PREFERENCES_OPEN_EVENT));
};
